import { useId, useRef, useState, type ComponentProps, type ReactNode, type Ref } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { cn, formatNumber, normalizeLocale } from '../lib/utils';

/**
 * Applied to the box on top of whatever the caller passes, so it wins: the amount fields sit on
 * button variants, and a filled variant reads as a selected amount, which fights the error.
 */
const errorBoxClassName =
	'border-destructive focus-within:border-destructive text-destructive focus-within:text-destructive bg-transparent hover:bg-transparent';

/**
 * Seeds `AmountInput.value` from a stored number. It has to go through the locale formatter:
 * `String(12.5)` would be read back as `125` where the decimal separator is a comma.
 */
export function amountToDisplay(locale: string, amount: number | undefined): string {
	if (amount == null || amount <= 0) return '';
	return formatNumber(locale, amount, Number.isInteger(amount) ? 0 : 2);
}

type AmountInputProps = Omit<
	ComponentProps<typeof CurrencyInput>,
	| 'value'
	| 'onValueChange'
	| 'intlConfig'
	| 'decimalSeparator'
	| 'groupSeparator'
	| 'suffix'
	| 'prefix'
	| 'className'
	| 'style'
	| 'ref'
	// Owned by `errorMessage`, so a caller cannot half-wire the pair.
	| 'aria-invalid'
	| 'aria-describedby'
	// The width comes from the mirror below, and the input is stretched over it, so a character count
	// would do nothing here except read as if it did something.
	| 'size'
> & {
	/** Locale driving the decimal/group separators, e.g. `es` renders `12,50`. */
	locale: string;
	/** Raw display string exactly as typed. Empty string shows the placeholder. */
	value: string;
	/** Called on every keystroke with the raw display string and its parsed number (0 when empty). */
	onValueChange: (display: string, amount: number) => void;
	/** Rendered right after the number, only once something is typed. */
	suffix?: ReactNode;
	/**
	 * Amount to auto fill when the empty field is focused, for a field that owns an amount already
	 * and would otherwise be left empty and invalid. Omit to leave the field empty, which is what a
	 * field offering a fresh amount wants: a caret and the currency, nothing to undo first.
	 */
	autoFillOnFocus?: number;
	/**
	 * Shown under the box when the amount is invalid, and the whole reason the field reports like
	 * every other one: it is what marks the input `aria-invalid`, points `aria-describedby` at the
	 * message and turns the box destructive. A form scrolling to the first `[aria-invalid="true"]`
	 * then lands on the input itself rather than skipping the amount.
	 */
	errorMessage?: ReactNode;
	/** Styles the wrapper, which is the visible box and the click target. */
	className?: string;
	/**
	 * Styles the amount itself. It lands on the sizer rather than on the input, so the hidden mirror
	 * the width is measured from inherits the same typography as the text it is measuring.
	 */
	inputClassName?: string;
	/** Forwarded to the input, so `react-hook-form` can focus the field it refuses to submit. */
	ref?: Ref<HTMLInputElement>;
};

/**
 * Locale-aware amount input, extracted from the recurring donation flow.
 *
 * Two things make it behave better than a bare `CurrencyInput`: the caller owns the raw display
 * string (binding `value` to a number breaks decimal commas mid-typing, so the parsed number is
 * handed back separately), and the suffix is real text next to the input instead of a suffix
 * inside it, so the number stays optically centred while it grows.
 *
 * It never rejects what is typed. Limits belong to the caller's validation, so an amount that is
 * too high shows an error instead of silently refusing the keystroke.
 */
export function AmountInput({
	locale,
	value,
	onValueChange,
	suffix = '€',
	autoFillOnFocus,
	errorMessage,
	decimalsLimit = 2,
	allowNegativeValue = false,
	className,
	inputClassName,
	placeholder,
	onFocus,
	onBlur,
	ref,
	...props
}: AmountInputProps) {
	const inputRef = useRef<HTMLInputElement>(null);
	const errorId = useId();
	const [isFocused, setIsFocused] = useState(false);
	const hasValue = value !== '';
	const hasError = !!errorMessage;

	// Focusing drops the placeholder so the caret sits right next to the suffix, which is what makes
	// the currency read as already there, waiting for a number, instead of a bare caret.
	const shownText = hasValue ? value : isFocused ? '' : (placeholder ?? '');

	return (
		<div className="flex flex-col gap-1">
			<div
				// The trailing classes are invariants of the box, so they win over whatever the caller
				// passes: it is a text surface, and the gap between number and suffix is the sizer's slack.
				className={cn(
					'flex items-center justify-center',
					className,
					'cursor-text gap-0 whitespace-nowrap hover:cursor-text',
					hasError && errorBoxClassName,
				)}
				onClick={(event) => {
					// Clicking the padding around the auto-sized input still focuses it.
					if (!(event.target as HTMLElement).closest('input')) inputRef.current?.focus();
				}}
			>
				{/*
				 * The field is sized by an invisible mirror of the text it is showing, with `px-[3px]` of
				 * slack around it, and the input is laid over that box instead of sizing itself.
				 *
				 * `field-sizing: content` is the obvious tool and is what used to be here, but it hugs the
				 * glyphs too tightly to survive a caret: WebKit keeps ~2px of scrollable overflow for one,
				 * so a focused field scrolls a pixel to reveal it and shaves the leading digit. That is the
				 * amount cut from the left on iPhone, and padding cannot buy the room back, because
				 * `field-sizing` subtracts padding from the width it computes. The slack is what makes a
				 * mirror safe where earlier attempts were not: the box is a few pixels wider than the text,
				 * so neither the caret nor a sub-pixel measuring difference can reach a glyph.
				 */}
				{/* The slack goes last so a caller's padding cannot land on the sizer and undo it. */}
				<span className={cn('relative inline-block min-w-0 shrink overflow-hidden', inputClassName, 'px-[3px]')}>
					<span aria-hidden="true" className="invisible block whitespace-pre">
						{/*
						 * An empty field still has to reserve a line, or the sizer collapses to its slack alone. A
						 * non-breaking space, so nothing downstream can read it as strippable whitespace.
						 */}
						{shownText === '' ? '\u00A0' : shownText}
					</span>
					<CurrencyInput
						ref={(node: HTMLInputElement | null) => {
							inputRef.current = node;
							if (typeof ref === 'function') ref(node);
							else if (ref) ref.current = node;
						}}
						// Covering the sizer exactly, padding included, is what hands the input the slack.
						className="absolute inset-0 w-full bg-transparent text-center text-inherit outline-none placeholder:text-inherit"
						placeholder={isFocused ? undefined : placeholder}
						value={value}
						intlConfig={{ locale: normalizeLocale(locale) }}
						decimalsLimit={decimalsLimit}
						allowNegativeValue={allowNegativeValue}
						step={1}
						onValueChange={(display, _name, values) => onValueChange(display ?? '', values?.float ?? 0)}
						onFocus={(event) => {
							onFocus?.(event);
							setIsFocused(true);
							if (!hasValue && autoFillOnFocus != null && autoFillOnFocus > 0) {
								onValueChange(amountToDisplay(locale, autoFillOnFocus), autoFillOnFocus);
							}
							// Caret at the end rather than wherever the tap landed: a centred amount reads as one
							// token, so there is no meaningful place to insert inside it. Selecting the amount instead
							// would be worse on mobile, where it raises the selection handles and the paste bubble.
							const input = event.currentTarget;
							requestAnimationFrame(() => input.setSelectionRange(input.value.length, input.value.length));
						}}
						onBlur={(event) => {
							onBlur?.(event);
							setIsFocused(false);
						}}
						{...props}
						aria-invalid={hasError}
						aria-describedby={hasError ? errorId : undefined}
					/>
				</span>
				{/*
				 * The suffix holds its size and the sizer above absorbs the shrinking, so a number too long
				 * for the box narrows the field it is typed in rather than pushing the currency out of it.
				 */}
				{(hasValue || isFocused) && suffix != null && (
					<span className="pointer-events-none shrink-0 select-none">{suffix}</span>
				)}
			</div>
			{/*
			 * Same markup `FieldInfo` renders, deliberately: one message slot should not sound different
			 * from the other. No `role="alert"` in particular, because the amount is validated on every
			 * keystroke and an assertive region would talk over the donor while they are still typing.
			 * `aria-describedby` above is what carries the message to a screen reader.
			 */}
			{hasError && (
				<p id={errorId} data-slot="form-message" className="text-destructive m-1 text-sm">
					{errorMessage}
				</p>
			)}
		</div>
	);
}
